/**
 * Converts a string to camelCase.
 *
 * This function:
 * - Normalizes all characters to lowercase
 * - Removes non-word characters (e.g., space, underscore, dash)
 * - Capitalizes each word except the first
 * - Returns a normalized, lowercase-first camelCase result
 *
 * @param text - The input string to convert.
 * @returns A camelCased version of the string.
 *
 * @example
 * camelCase('hello world');     // 'helloWorld'
 * camelCase('My_string-value'); // 'myStringValue'
 * camelCase('alreadyCamel');    // 'alreadycamel'
 * camelCase('FOO_bar_baz');     // 'fooBarBaz'
 */
export function camelCase(text: string): string {
  const parts = text
    .replace(/[^a-zA-Z0-9]+/g, ' ')
    .trim()
    .toLowerCase()
    .split(' ');

  return parts[0] + parts.slice(1).map(capitalize).join('');
}

/**
 * Converts a string to PascalCase.
 *
 * This function:
 * - Normalizes all characters to lowercase
 * - Removes non-word characters (e.g., space, underscore, dash)
 * - Capitalizes each word including the first
 * - Returns a normalized PascalCase result
 *
 * @param text - The input string to convert.
 * @returns A PascalCased version of the string.
 *
 * @example
 * pascalCase('hello world');     // 'HelloWorld'
 * pascalCase('my_string-value'); // 'MyStringValue'
 * pascalCase('AlreadyPascal');   // 'Alreadypascal'
 * pascalCase('FOO_bar_baz');     // 'FooBarBaz'
 */
export function pascalCase(text: string): string {
  return text
    .replace(/[^a-zA-Z0-9]+/g, ' ')
    .trim()
    .toLowerCase()
    .split(' ')
    .map(capitalize)
    .join('');
}

/**
 * Capitalizes the first character of a string and leaves the rest unchanged.
 *
 * If the input is empty, it returns an empty string.
 *
 * @param text - The input string to capitalize.
 * @returns The input string with the first character capitalized.
 *
 * @example
 * capitalize('hello');   // 'Hello'
 * capitalize('x');       // 'X'
 * capitalize('already'); // 'Already'
 * capitalize('');        // ''
 */
export function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * Checks whether a value is `null`, `undefined`, or a string containing only whitespace.
 *
 * This is useful for treating empty or whitespace-only strings as equivalent to missing values.
 *
 * @param text - The value to check.
 * @returns `true` if the value is `null`, `undefined`, or a string of only whitespace.
 *
 * @example
 * isNilOrWhitespace(null);      // true
 * isNilOrWhitespace(undefined); // true
 * isNilOrWhitespace('');        // true
 * isNilOrWhitespace('   ');     // true
 * isNilOrWhitespace('hello');   // false
 * isNilOrWhitespace(false);     // false
 */
export function isNilOrWhitespace(text: null | undefined | string): boolean {
  return text == null || text.trim().length === 0;
}
